import { UserRoundX } from 'lucide-react'
import { useMemo } from 'react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'

import Avatar from '@/elements/Avatar'
import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Pill from '@/elements/Pill'
import Text from '@/elements/Text'
import { defaultColumnOptions, type TableAny } from '@/utils/tables'
import type { IndividualAssignmentResponse } from '@/api/types/Organization'

interface IndividualInnerCircleTableType {
  id: IndividualAssignmentResponse['id']
  avatarImg?: string
  name: IndividualAssignmentResponse['details']['fullName']
  role?: string
}

const columnHelper = createColumnHelper<IndividualInnerCircleTableType>()

export function useIndividualInnerCircleTable(
  innerCircle: IndividualAssignmentResponse[],
  onRemoveInnerCircle: (id: string) => void
) {
  const data: IndividualInnerCircleTableType[] = useMemo(() => (
    innerCircle ? innerCircle.map(row => ({
      id: row?.id,
      avatarImg: row?.assets?.PROFILE_PICTURE || undefined,
      name: row?.details?.fullName || '',
      role: 'View Only'
    })) : []
  ), [innerCircle])

  const columns: ColumnDef<IndividualInnerCircleTableType, TableAny>[] = [
    columnHelper.accessor('name', {
      ...defaultColumnOptions,
      header: 'Name',
      enableSorting: false,
      cell: info => (
        <Flex inline align='center' gap={2} className='user-cell'>
          <Avatar
            name={info.getValue() as string}
            src={info.row.original.avatarImg}
            size='extraSmall'
            color='gray'
          />
          <Text variant='textblock'>{info.getValue() as string}</Text>
        </Flex>
      )
    }),
    columnHelper.accessor('role', {
      ...defaultColumnOptions(),
      header: 'Role',
      enableSorting: false,
      cell: info => {
        return (
          <Pill variant='neutral'>{info.getValue()}</Pill>
        )
      }
    }),
    columnHelper.display({
      id: 'actions',
      ...defaultColumnOptions(),
      header: 'Actions',
      meta: { width: 100 },
      enableSorting: false,
      cell: info => {
        const innerCircleId = info.row.original.id
        return (
          <Flex justify='center' align='center' className='actions-cell'>
            <Button
              small
              variant='iconDangerBtn'
              onClick={() => onRemoveInnerCircle(innerCircleId)}
            >
              <UserRoundX size={18} />
            </Button>
          </Flex>
        )
      }
    }),
  ]

  return { data, columns }
}
